import classNames from 'clsx';
import React from 'react';

import type { TennisMatch } from '@swf/generated/graphql/scoreboards/schemas';
import { Icon } from '@swf/sports/components';
import type { Participants } from '@swf/sports/types/event';

import styles from './TennisScoreboard.scss';

const isNumber = (value: unknown): value is number => Number.isInteger(value);
const isScoreValid = (score: unknown): boolean => (isNumber(score) && score >= 0) || !!score;

interface Props {
  participants: Participants;
  tennisMatch: TennisMatch;
  isValidInPlayStatus?: boolean;
}

const TennisScoreboard: React.FC<Props> = ({ participants, tennisMatch, isValidInPlayStatus }) => {
  const [teamA, teamB] = participants;
  const { teamAScore, teamBScore, currentSet } = tennisMatch;

  const currentTeamServing = currentSet?.currentGame?.teamServing;

  const isTeamAServing = currentTeamServing === 'HOME';
  const isTeamBServing = currentTeamServing === 'AWAY';
  const isSomeoneServing = isTeamAServing || isTeamBServing;
  const isTieBreak = currentSet?.currentGame?.type === 'TIEBREAK';

  return (
    <div
      data-testid="tennis-scoreboard"
      className={classNames({ [styles.inPlay]: isValidInPlayStatus })}
    >
      <div className={styles.teamContainer}>
        <span className={styles.score}>{teamAScore ?? '0'} </span>

        {isScoreValid(currentSet?.teamAScore) && (
          <span className={classNames(styles.score, styles.gameScore)}>
            {currentSet?.teamAScore}{' '}
          </span>
        )}

        {isScoreValid(currentSet?.currentGame?.teamAScore) && (
          <span
            className={classNames(styles.score, styles.pointsScore, styles.pointsScoreTeamA, {
              [styles.tieBreak]: isTieBreak
            })}
          >
            {currentSet?.currentGame?.teamAScore}{' '}
          </span>
        )}

        <div className={styles.icon}>{isTeamAServing && <Icon name="marker" size="x2" />}</div>

        <span
          className={classNames(styles.teamName, {
            [styles.otherTeamServing]: !isTeamAServing && isTeamBServing,
            [styles.nobodyServing]: !isSomeoneServing
          })}
        >
          {teamA}
        </span>
      </div>

      <div className={styles.teamContainer}>
        <span className={styles.score}>{teamBScore ?? '0'} </span>

        {isScoreValid(currentSet?.teamBScore) && (
          <span className={classNames(styles.score, styles.gameScore)}>
            {currentSet?.teamBScore}{' '}
          </span>
        )}

        {isScoreValid(currentSet?.currentGame?.teamBScore) && (
          <span
            className={classNames(styles.score, styles.pointsScore, styles.pointsScoreTeamB, {
              [styles.tieBreak]: isTieBreak
            })}
          >
            {currentSet?.currentGame?.teamBScore}{' '}
          </span>
        )}

        <div className={styles.icon}>{isTeamBServing && <Icon name="marker" size="x2" />}</div>

        <span
          className={classNames(styles.teamName, {
            [styles.otherTeamServing]: !isTeamBServing,
            [styles.nobodyServing]: !isSomeoneServing
          })}
        >
          {teamB}
        </span>
      </div>
    </div>
  );
};

export default TennisScoreboard;
